import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { COLOR_THEMES } from '../constants/colorThemes';

const ColorEditor = ({ 
  value, 
  onChange, 
  label = "Color Theme",
  showReset = true,
  resetValue = "default",
  siteTheme = null,
  customThemes = [],
  onCreateCustomTheme = null
}) => {

  const predefinedOptions = COLOR_THEMES;
  
  // Combine all available themes
  const allThemes = [
    // Add site theme as "default" option if available
    siteTheme ? {
      name: 'Default',
      colors: siteTheme.colors || siteTheme,
      isSiteDefault: true
    } : null,
    ...predefinedOptions,
    ...customThemes.map(theme => ({
      ...theme,
      isCustom: true,
      preview: {
        bg: theme.colors.background,
        border: theme.colors.accent,
        text: theme.colors.text,
        accent: theme.colors.primary
      }
    }))
  ].filter(Boolean);

  // Get the selected theme
  const getSelectedTheme = () => {
    return allThemes.find(theme => theme.name === value) || allThemes[0];
  };
  
  const selectedTheme = getSelectedTheme();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      </div>
      
      {/* Current Selection Preview */}
      <div 
        className="w-full h-12 rounded-lg border-2 p-3 flex items-center justify-between"
        style={{
          backgroundColor: selectedTheme.colors.background,
          borderColor: selectedTheme.colors.accent,
          color: selectedTheme.colors.text
        }}
      >
        <div className="flex items-center space-x-3">
          {/* 5-color preview */}
          <div className="flex space-x-1">
            <div 
              className="w-4 h-4 rounded border border-gray-200" 
              style={{ backgroundColor: selectedTheme.colors?.primary || selectedTheme.primary }}
              title="Primary"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-200" 
              style={{ backgroundColor: selectedTheme.colors?.secondary || selectedTheme.secondary }}
              title="Secondary"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-200" 
              style={{ backgroundColor: selectedTheme.colors?.accent || selectedTheme.accent }}
              title="Accent"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-200" 
              style={{ backgroundColor: selectedTheme.colors?.background || selectedTheme.background }}
              title="Background"
            />
            <div 
              className="w-4 h-4 rounded border border-gray-200" 
              style={{ backgroundColor: selectedTheme.colors?.text || selectedTheme.text }}
              title="Text"
            />
          </div>
          <div>
            <div className="text-sm font-medium flex items-center">
              {selectedTheme.name}
              {selectedTheme.isSiteDefault && <span className="ml-1">🌐</span>}
              {selectedTheme.isCustom && <span className="ml-1">🎨</span>}
              {value !== resetValue && !selectedTheme.isSiteDefault && <span className="ml-1 text-xs text-orange-600">⚡</span>}
            </div>
          </div>
        </div>
        
      </div>

      {/* Theme Selector */}
  
        <div className="mt-3 space-y-2">
          {/* All Available Themes */}
          <div className="all-themes">
            <div className="grid grid-cols-4 gap-1">
              {allThemes.map((theme) => (
                <button
                  key={theme.name}
                  onClick={() => {
                    onChange(theme.name);
                    
                  }}
                  className={`
                    group relative w-full h-16 rounded-lg border-2 overflow-hidden transition-all duration-200
                    ${value === theme.name 
                      ? `ring-2 ${theme.isCustom ? 'ring-purple-500' : 'ring-gray-500'} ring-offset-1 scale-90`
                      : 'hover:scale-102 hover:shadow-md'
                    }
                  `}
                  style={{
                    backgroundColor: theme.colors.background,
                    borderColor: theme.colors.accent
                  }}
                  title={theme.name}
                >
                  {/* Color indicators */}
                  <div className="absolute top-1 left-1 flex space-x-0.5">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.colors.primary }} />
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.colors.accent }} />
                  </div>
                  
                  {/* Theme name */}
                  <div className="absolute bottom-1 right-1 text-xs font-medium opacity-60 group-hover:opacity-100 transition-opacity" style={{ color: theme.colors.text }}>
                    {theme.name?.split(' ')[0]}
                  </div>
                  
                  {/* Selection indicator */}
                  {value === theme.name && (
                    <div className={`absolute inset-0 ${theme.isCustom ? 'bg-purple-500' : 'bg-blue-500'} bg-opacity-10 flex items-center justify-center`}>
                      <div className={`w-5 h-5 ${theme.isCustom ? 'bg-purple-500' : 'bg-blue-500'} rounded-full flex items-center justify-center`}>
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Theme type indicators */}
                  <div className="absolute top-1 right-1 text-xs">
                    {theme.isSiteDefault && "🌐"}
                    {theme.isCustom && "🎨"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Create custom theme link */}
          {onCreateCustomTheme && (
            <div className="create-theme-link">
              <button 
                onClick={() => {
                  onCreateCustomTheme();
                }}
                className="w-full p-3 text-sm text-purple-600 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
              >
                + Custom Theme
              </button>
            </div>
          )}
        </div>



    </div>
  );
};

export default ColorEditor;